import type { CandidateProgramFlags } from '../config/programFlags';

export type CandidateProgramFilterValues = {
  admittedFilter: 'all' | 'y' | 'n';
  graduatedFilter: 'y' | 'n';
  mistReleasedFilter: 'all' | 'y' | 'n';
  dispoFilter: 'all' | 'cbr' | 'off_ramp' | 'pc100136' | 'cancelled';
  trackFilter: 'all' | 'any' | 'CBR' | 'Off-Ramp' | 'PCU' | '1001.36' | 'Other';
  mdFilter: 'all' | 'any' | 'none';
  clinicianFilter: 'all' | 'any' | 'none';
  vetFilter: 'all' | 'Yes' | 'No';
  departmentFilter: 'all' | 'hub1' | 'hub2' | 'hub3';
  dshCancelledFilter: 'y' | 'n';
  odrHubFilter: 'all' | 'hub1' | 'hub2' | 'hub3';
  odrMhdFilter: 'all' | 'y' | 'n';
  odrReviewStatusFilter: string;
  odrWorkflowFilter: string;
  odrReviewerFilter: string;
  odrEmailedPdFilter: 'all' | 'y' | 'n';
  odrAddedToDdFilter: 'all' | 'y' | 'n';
  odrEligibleFilter: 'all' | 'y' | 'n';
  odrHomelessFilter: 'all' | 'y' | 'n';
  odrPrePleaFilter: 'all' | 'y' | 'n';
  odrCancelledFilter: 'all' | 'y' | 'n';
  odrEnrolledFilter: 'all' | 'y' | 'n';
  odrIhFilter: 'all' | 'Y' | 'N';
  odrImdFilter: 'all' | 'Y' | 'N';
  covidGroupFilter: string;
  covidDispositionFilter: string;
  covidRetainFilter: 'all' | 'y' | 'n';
  covidReleasedFilter: 'all' | 'y' | 'n';
  covidEnrolledFilter: 'all' | 'y' | 'n';
  covidCompletedFilter: 'all' | 'y' | 'n';
  covidJacketReadyFilter: 'all' | 'y' | 'n';
  covidReleasePassSentFilter: 'all' | 'y' | 'n';
  covidTestingTypeFilter: string;
  covidTestingSourceFilter: 'all' | 'staff' | 'client';
  covidTestingProgramFilter: string;
  covidTestingResultFilter: string;
  covidTestingClosedFilter: 'all' | 'y' | 'n';
  covidVaccineSourceFilter: 'all' | 'staff' | 'client';
  covidVaccineProgramFilter: string;
  covidVaccineShot1Filter: 'all' | 'Yes' | 'None';
  covidVaccineShot2Filter: 'all' | 'Yes' | 'None';
  influenzaProgramFilter: string;
  influenzaNotReceivedFilter: string;
  maternalIneligibleFilter: 'y' | 'n';
  inrepClosedFilter: 'all' | 'y' | 'n';
  inrepProgramFilter: string;
  progressReportSubmittedFilter: 'all' | 'y' | 'n';
  progressReportCompletedFilter: 'all' | 'y' | 'n';
  progressReportHubFilter: string;
  progressReportAppearanceFilter: string;
  progressReportIcmsProviderFilter: string;
  progressReportCourtDateFilter: string;
  laiFacilityFilter: 'all' | 'TTCF' | 'CRDF' | 'MCJ' | 'NCCF/PDC';
  laiCommunityLocationFilter: 'all' | 'PB-PSH' | 'IH+SS-PSH+BC+Other';
};

export const DEFAULT_CANDIDATE_PROGRAM_FILTERS: CandidateProgramFilterValues = {
  admittedFilter: 'all',
  graduatedFilter: 'n',
  mistReleasedFilter: 'n',
  dispoFilter: 'all',
  trackFilter: 'all',
  mdFilter: 'all',
  clinicianFilter: 'all',
  vetFilter: 'all',
  departmentFilter: 'hub1',
  dshCancelledFilter: 'n',
  odrHubFilter: 'all',
  odrMhdFilter: 'all',
  odrReviewStatusFilter: 'all',
  odrWorkflowFilter: 'all',
  odrReviewerFilter: '',
  odrEmailedPdFilter: 'all',
  odrAddedToDdFilter: 'n',
  odrEligibleFilter: 'all',
  odrHomelessFilter: 'all',
  odrPrePleaFilter: 'all',
  odrCancelledFilter: 'all',
  odrEnrolledFilter: 'all',
  odrIhFilter: 'all',
  odrImdFilter: 'all',
  covidGroupFilter: 'all',
  covidDispositionFilter: 'all',
  covidRetainFilter: 'all',
  covidReleasedFilter: 'all',
  covidEnrolledFilter: 'all',
  covidCompletedFilter: 'all',
  covidJacketReadyFilter: 'all',
  covidReleasePassSentFilter: 'all',
  covidTestingTypeFilter: 'all',
  covidTestingSourceFilter: 'all',
  covidTestingProgramFilter: 'all',
  covidTestingResultFilter: 'all',
  covidTestingClosedFilter: 'all',
  covidVaccineSourceFilter: 'all',
  covidVaccineProgramFilter: 'all',
  covidVaccineShot1Filter: 'all',
  covidVaccineShot2Filter: 'all',
  influenzaProgramFilter: 'all',
  influenzaNotReceivedFilter: 'all',
  maternalIneligibleFilter: 'n',
  inrepClosedFilter: 'all',
  inrepProgramFilter: 'all',
  progressReportSubmittedFilter: 'all',
  progressReportCompletedFilter: 'all',
  progressReportHubFilter: 'all',
  progressReportAppearanceFilter: 'all',
  progressReportIcmsProviderFilter: '',
  progressReportCourtDateFilter: '',
  laiFacilityFilter: 'all',
  laiCommunityLocationFilter: 'all',
};

export type CandidateProgramFilterSetters = {
  [K in keyof CandidateProgramFilterValues]: (value: CandidateProgramFilterValues[K]) => void;
};

export const resetCandidateProgramFilters = (setters: CandidateProgramFilterSetters) => {
  setters.admittedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.admittedFilter);
  setters.graduatedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.graduatedFilter);
  setters.mistReleasedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.mistReleasedFilter);
  setters.dispoFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.dispoFilter);
  setters.trackFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.trackFilter);
  setters.mdFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.mdFilter);
  setters.clinicianFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.clinicianFilter);
  setters.vetFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.vetFilter);
  setters.departmentFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.departmentFilter);
  setters.dshCancelledFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.dshCancelledFilter);
  setters.odrHubFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrHubFilter);
  setters.odrMhdFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrMhdFilter);
  setters.odrReviewStatusFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrReviewStatusFilter);
  setters.odrWorkflowFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrWorkflowFilter);
  setters.odrReviewerFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrReviewerFilter);
  setters.odrEmailedPdFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrEmailedPdFilter);
  setters.odrAddedToDdFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrAddedToDdFilter);
  setters.odrEligibleFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrEligibleFilter);
  setters.odrHomelessFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrHomelessFilter);
  setters.odrPrePleaFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrPrePleaFilter);
  setters.odrCancelledFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrCancelledFilter);
  setters.odrEnrolledFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrEnrolledFilter);
  setters.odrIhFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrIhFilter);
  setters.odrImdFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.odrImdFilter);
  setters.covidGroupFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidGroupFilter);
  setters.covidDispositionFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidDispositionFilter);
  setters.covidRetainFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidRetainFilter);
  setters.covidReleasedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidReleasedFilter);
  setters.covidEnrolledFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidEnrolledFilter);
  setters.covidCompletedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidCompletedFilter);
  setters.covidJacketReadyFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidJacketReadyFilter);
  setters.covidReleasePassSentFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidReleasePassSentFilter);
  setters.covidTestingTypeFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidTestingTypeFilter);
  setters.covidTestingSourceFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidTestingSourceFilter);
  setters.covidTestingProgramFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidTestingProgramFilter);
  setters.covidTestingResultFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidTestingResultFilter);
  setters.covidTestingClosedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidTestingClosedFilter);
  setters.covidVaccineSourceFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidVaccineSourceFilter);
  setters.covidVaccineProgramFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidVaccineProgramFilter);
  setters.covidVaccineShot1Filter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidVaccineShot1Filter);
  setters.covidVaccineShot2Filter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.covidVaccineShot2Filter);
  setters.influenzaProgramFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.influenzaProgramFilter);
  setters.influenzaNotReceivedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.influenzaNotReceivedFilter);
  setters.maternalIneligibleFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.maternalIneligibleFilter);
  setters.inrepClosedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.inrepClosedFilter);
  setters.inrepProgramFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.inrepProgramFilter);
  setters.progressReportSubmittedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.progressReportSubmittedFilter);
  setters.progressReportCompletedFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.progressReportCompletedFilter);
  setters.progressReportHubFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.progressReportHubFilter);
  setters.progressReportAppearanceFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.progressReportAppearanceFilter);
  setters.progressReportIcmsProviderFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.progressReportIcmsProviderFilter);
  setters.progressReportCourtDateFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.progressReportCourtDateFilter);
  setters.laiFacilityFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.laiFacilityFilter);
  setters.laiCommunityLocationFilter(DEFAULT_CANDIDATE_PROGRAM_FILTERS.laiCommunityLocationFilter);
};

type BuildCandidateListVariablesArgs = {
  component: string;
  page: number;
  pageSize: number;
  search: string;
  searchField: string;
  flags: CandidateProgramFlags;
  filters: CandidateProgramFilterValues;
  overrides?: Record<string, any>;
};

export const buildCandidateListQueryVariables = ({
  component,
  page,
  pageSize,
  search,
  searchField,
  flags,
  filters,
  overrides = {},
}: BuildCandidateListVariablesArgs) => ({
  component,
  page,
  pageSize,
  search: search || null,
  searchField: searchField !== 'all' ? searchField : null,
  admitted: flags.isOliveFamily ? filters.admittedFilter : null,
  graduated: flags.isOliveFamily ? filters.graduatedFilter : null,
  dispo: flags.isFistProgram ? filters.dispoFilter : null,
  track: flags.isFistProgram ? filters.trackFilter : null,
  md: flags.isFistProgram ? filters.mdFilter : null,
  clinician: flags.isFistProgram ? filters.clinicianFilter : null,
  vet: flags.isFistProgram ? filters.vetFilter : null,
  released: flags.isMistProgram ? filters.mistReleasedFilter : flags.isCovidProgram ? filters.covidReleasedFilter : null,
  department: flags.isDshProgram ? filters.departmentFilter : null,
  cancelled: flags.isDshProgram ? filters.dshCancelledFilter : flags.isOdrProgram ? filters.odrCancelledFilter : null,
  hub: flags.isOdrProgram ? filters.odrHubFilter : null,
  odrMhd: flags.isOdrProgram ? filters.odrMhdFilter : null,
  reviewStatus: flags.isOdrProgram ? filters.odrReviewStatusFilter : null,
  workflowProgress: flags.isOdrProgram ? filters.odrWorkflowFilter : null,
  reviewer: flags.isOdrProgram ? filters.odrReviewerFilter || null : null,
  emailedPd: flags.isOdrProgram ? filters.odrEmailedPdFilter : null,
  addedToDd: flags.isOdrProgram ? (search ? 'all' : filters.odrAddedToDdFilter) : null,
  eligible: flags.isOdrProgram ? filters.odrEligibleFilter : null,
  homeless: flags.isOdrProgram ? filters.odrHomelessFilter : null,
  prePlea: flags.isOdrProgram ? filters.odrPrePleaFilter : null,
  enrolled: flags.isOdrProgram ? filters.odrEnrolledFilter : flags.isCovidProgram ? filters.covidEnrolledFilter : null,
  ih: flags.isOdrProgram ? filters.odrIhFilter : null,
  imd: flags.isOdrProgram ? filters.odrImdFilter : null,
  covidGroup: flags.isCovidProgram ? filters.covidGroupFilter : null,
  disposition: flags.isCovidProgram ? filters.covidDispositionFilter : null,
  retain: flags.isCovidProgram ? filters.covidRetainFilter : null,
  dateInmateLeft: flags.isCovidProgram ? filters.covidCompletedFilter : null,
  jacketReady: flags.isCovidProgram ? filters.covidJacketReadyFilter : null,
  releasePassSent: flags.isCovidProgram ? filters.covidReleasePassSentFilter : null,
  testType: flags.isCovidTestingProgram ? filters.covidTestingTypeFilter : null,
  clientOrStaff: flags.isCovidTestingProgram
    ? filters.covidTestingSourceFilter
    : flags.isCovidVaccineFamily
      ? filters.covidVaccineSourceFilter
      : null,
  program: flags.isCovidTestingProgram
    ? filters.covidTestingProgramFilter
    : flags.isCovidVaccineFamily
      ? filters.covidVaccineProgramFilter
      : flags.isInfluenzaVaccineProgram
        ? filters.influenzaProgramFilter
        : null,
  testResult: flags.isCovidTestingProgram ? filters.covidTestingResultFilter : null,
  shot1Status: flags.isCovidVaccineFamily ? filters.covidVaccineShot1Filter : null,
  shot2Status: flags.isCovidVaccineFamily ? filters.covidVaccineShot2Filter : null,
  notReceived: flags.isInfluenzaVaccineProgram ? filters.influenzaNotReceivedFilter : null,
  currentlyIneligible: flags.isMaternalProgram ? filters.maternalIneligibleFilter : null,
  closed: flags.isInrepProgram ? filters.inrepClosedFilter : flags.isCovidTestingProgram ? filters.covidTestingClosedFilter : null,
  inrepProgram: flags.isInrepProgram ? filters.inrepProgramFilter : null,
  progressIsDraft: flags.isProgressReportProgram ? filters.progressReportSubmittedFilter : null,
  progressCompleted: flags.isProgressReportProgram ? filters.progressReportCompletedFilter : null,
  progressHub: flags.isProgressReportProgram ? filters.progressReportHubFilter : null,
  progressAppearance: flags.isProgressReportProgram ? filters.progressReportAppearanceFilter : null,
  icmsProvider: flags.isProgressReportProgram ? filters.progressReportIcmsProviderFilter || null : null,
  progressCourtDate: flags.isProgressReportProgram ? filters.progressReportCourtDateFilter || null : null,
  facility: flags.isLaiProgram ? filters.laiFacilityFilter : null,
  location: flags.isLaiCommunityProgram ? filters.laiCommunityLocationFilter : null,
  sortField: overrides.sortField ?? 'updated',
  sortDirection: overrides.sortDirection ?? 'DESC',
  ...overrides,
});

type HasActiveProgramFiltersArgs = {
  searchField: string;
  flags: CandidateProgramFlags;
  filters: CandidateProgramFilterValues;
};

export const hasActiveCandidateProgramFilters = ({
  searchField,
  flags,
  filters,
}: HasActiveProgramFiltersArgs): boolean => {
  if (searchField !== 'all') {
    return true;
  }

  if (flags.isOliveFamily && (filters.admittedFilter !== 'all' || filters.graduatedFilter !== 'n')) {
    return true;
  }

  if (flags.isMistProgram && filters.mistReleasedFilter !== 'n') {
    return true;
  }

  if (
    flags.isFistProgram &&
    (filters.dispoFilter !== 'all' ||
      filters.trackFilter !== 'all' ||
      filters.mdFilter !== 'all' ||
      filters.clinicianFilter !== 'all' ||
      filters.vetFilter !== 'all')
  ) {
    return true;
  }

  if (flags.isDshProgram && (filters.departmentFilter !== 'hub1' || filters.dshCancelledFilter !== 'n')) {
    return true;
  }

  if (
    flags.isOdrProgram &&
    (filters.odrHubFilter !== 'all' ||
      filters.odrMhdFilter !== 'all' ||
      filters.odrReviewStatusFilter !== 'all' ||
      filters.odrWorkflowFilter !== 'all' ||
      filters.odrReviewerFilter.trim() !== '' ||
      filters.odrEmailedPdFilter !== 'all' ||
      filters.odrAddedToDdFilter !== 'n' ||
      filters.odrEligibleFilter !== 'all' ||
      filters.odrHomelessFilter !== 'all' ||
      filters.odrPrePleaFilter !== 'all' ||
      filters.odrCancelledFilter !== 'all' ||
      filters.odrEnrolledFilter !== 'all' ||
      filters.odrIhFilter !== 'all' ||
      filters.odrImdFilter !== 'all')
  ) {
    return true;
  }

  if (
    flags.isCovidProgram &&
    (filters.covidGroupFilter !== 'all' ||
      filters.covidDispositionFilter !== 'all' ||
      filters.covidRetainFilter !== 'all' ||
      filters.covidReleasedFilter !== 'all' ||
      filters.covidEnrolledFilter !== 'all' ||
      filters.covidCompletedFilter !== 'all' ||
      filters.covidJacketReadyFilter !== 'all' ||
      filters.covidReleasePassSentFilter !== 'all')
  ) {
    return true;
  }

  if (
    flags.isCovidTestingProgram &&
    (filters.covidTestingTypeFilter !== 'all' ||
      filters.covidTestingSourceFilter !== 'all' ||
      filters.covidTestingProgramFilter !== 'all' ||
      filters.covidTestingResultFilter !== 'all' ||
      filters.covidTestingClosedFilter !== 'all')
  ) {
    return true;
  }

  if (
    flags.isCovidVaccineFamily &&
    (filters.covidVaccineSourceFilter !== 'all' ||
      filters.covidVaccineProgramFilter !== 'all' ||
      filters.covidVaccineShot1Filter !== 'all' ||
      filters.covidVaccineShot2Filter !== 'all')
  ) {
    return true;
  }

  if (
    flags.isInfluenzaVaccineProgram &&
    (filters.influenzaProgramFilter !== 'all' || filters.influenzaNotReceivedFilter !== 'all')
  ) {
    return true;
  }

  if (
    flags.isProgressReportProgram &&
    (filters.progressReportSubmittedFilter !== 'all' ||
      filters.progressReportCompletedFilter !== 'all' ||
      filters.progressReportHubFilter !== 'all' ||
      filters.progressReportAppearanceFilter !== 'all' ||
      filters.progressReportIcmsProviderFilter.trim() !== '' ||
      filters.progressReportCourtDateFilter.trim() !== '')
  ) {
    return true;
  }

  if (flags.isMaternalProgram && filters.maternalIneligibleFilter !== 'n') {
    return true;
  }

  if (flags.isInrepProgram && (filters.inrepClosedFilter !== 'all' || filters.inrepProgramFilter !== 'all')) {
    return true;
  }

  if (flags.isLaiProgram && filters.laiFacilityFilter !== 'all') {
    return true;
  }

  if (flags.isLaiCommunityProgram && filters.laiCommunityLocationFilter !== 'all') {
    return true;
  }

  return false;
};

export const getCandidateAddButtonLabel = (flags: CandidateProgramFlags): string => {
  if (flags.isInrepProgram) {
    return '+ File Incident Report';
  }

  if (flags.isProgressReportProgram) {
    return '+ File Progress Report';
  }

  return '+ Add Candidate';
};
